import React from 'react';
import MainLayout from 'components/layout/MainLayout';
import BlogArticle from 'components/blog/BlogArticle';
import { ASSET_PREFIX } from 'utils/constants';
import { NextSeo } from 'next-seo';
import RoundedButton from 'components/RoundedButton';
import Separator from 'components/Separator';
import { getCta } from 'utils/common';

const Article = () => {
  const IMAGE_DIRECTORY = `${ASSET_PREFIX}/images/blog/articles/best-4-camping-spots-for-summer-2022`;

  return (
    <MainLayout additionalClassName="blogPost">
      <NextSeo
        title="The 4 Best Summer Camping Spots | Win Epic Prizes | DAYMADE"
        description="Discover the best 4 camping destinations in the UK to have an epic adventure this summer. Enter now for a chance to win your dream camping trip."
        openGraph={{
          title: 'The 4 Best Summer Camping Spots | Win Epic Prizes | DAYMADE',
          description:
            'Discover the best 4 camping destinations in the UK to have an epic adventure this summer. Enter now for a chance to win your dream camping trip.',
          type: 'article',
          url: 'https://www.daymade.co.uk/blog/best-4-camping-spots-for-summer-2022',
          images: [
            {
              url: `${IMAGE_DIRECTORY}/hero.jpg`,
              alt: 'A landscape view of a tent porch looking into the forest',
            },
          ],
        }}
        // additionalMetaTags={[{
        //   name: 'keywords',
        //   content: 'winner,lottery,daymade,jackpot,raffle,camping,adventure,travel,prize,win a prize,online,win a holiday,coolcamping',
        // }]}
      />

      <BlogArticle
        imageDirectory={IMAGE_DIRECTORY}
        heroAltText="A landscape view of a tent porch looking into the forest"
        heroImageYPosition="30%"
      >
        <>
          <h1 style={{ fontSize: '1.6em', lineHeight: '1.4em' }}>
            The Best 4 Camping Spots for Summer 2022
          </h1>

          <p className="subheading">
            Forget about escaping the UK. Get your inner outdoorsy thrill on
            right here.
          </p>

          <div
            className="separator"
            style={{ width: '80%', marginTop: 0 }}
          />

          <p
            style={{
              fontStyle: 'italic',
              textAlign: 'center',
              marginBottom: '2em',
            }}
          >
            by Violet Currey
          </p>

          <p>
            At DAYMADE, we're all about winning prizes, sharing memories with
            our faves, and reconnecting with nature. So what's the best activity
            to tick off all of these? Oh yes, it's camping! The summer has
            officially begun, and it's screaming camping weather. Over the last
            few years, we have learnt that there is so much unique natural
            beauty around the UK that some of us never knew existed. There are
            magical waterfalls, killer hikes, wild swimming, loads of fun sports
            and even fairies (Say what!), to list a few. We all thought we had
            to travel halfway across the world to experience some of mother
            nature's wonders. But nope, the UK is jam-packed, and it's on our
            doorstep. Pack your tent, grab your bags and bring your pals (or
            just yourself, why not!) and even your furry friends to these
            incredible camping spots. Here are our four fave camping spots for
            you to enjoy this summer:
          </p>

          <img
            src={`${IMAGE_DIRECTORY}/secondary1.jpg`}
            alt="A man camping watching the sunrise"
            className="singleBlogImage"
          />

          <h2>Brecon Beacons</h2>

          <p>
            The great Welsh countryside has something truly magical for
            everyone. It was a hard choice as Wales has an endless list of fun
            camping spots, but we have narrowed it down to the Brecon Beacons.
            The glorious mountain range is perfect for all levels of walkers,
            mountain hikers and nature lovers. One of the most exciting
            highlights if you head over to the Brecons for your next adventure
            is the Water Country. The Four Walks Waterfall is the famous
            waterfall which makes you feel like you're at Niagara Falls (okay,
            not quite - but close enough!) Pack your cozzy and jump in to enjoy
            some legit wild swimming. Explore the forests, peaks and wonderful
            lakes that the Beacons offer, or maybe you want to get your inner
            Tarzan on by riding the{' '}
            <a
              href="https://www.daymade.co.uk/prizes/details/zip-world-gift"
              target="_blank"
              rel="noreferrer"
            >
              Zip World Tower
            </a>
            . Head back to your campsite for some well-deserved R&R, crack open
            the barbie and enjoy the sweet peace and quiet. You have over 60
            camping locations to choose from in one of the best nature spots for
            some authentic Welsh beauty.
          </p>

          <img
            src={`${IMAGE_DIRECTORY}/secondary2.jpg`}
            alt="A waterfall in the Brecon Beacons National Park"
            className="singleBlogImage"
          />

          <h2>Cornwall</h2>

          <p>
            What better way to experience the UK's best beaches than popping
            your tent up along the coastline and waking up to that fresh sea
            breeze every morning? Of course, Cornwall, a.k.a the French Riviera,
            is on our top 5 list. It's the perfect solo trip where you can
            FINALLY be one of those cool Cali surfer dudes! Don't know how to
            surf? Don't worry; we've got you covered with our epic{' '}
            <a
              href="https://www.daymade.co.uk/prizes/details/surf-in-cornwall"
              target="_blank"
              rel="noreferrer"
            >
              Surfing & Wild Camping in Cornwall Vacation prize
            </a>
            . You can win experiences pushing you out of your comfort zone and
            get stuck in with our ultimate Cornwall summer adventure. Don't
            forget to treat yourself to some of the finest Cornish food whilst
            you're there. (Highly recommend a pasty or two!). This summer is
            your chance to experience the best beach camping in the UK.
          </p>

          <img
            src={`${IMAGE_DIRECTORY}/secondary3.jpg`}
            alt="People swimming in the sea at the beach in Cornwall"
            className="singleBlogImage"
          />

          <h2>Isle of Skye</h2>

          <p>
            We've got a special wee camping treasure up in the Isle of Skye.
            It’s one of the most popular places for camping in Scotland, and we
            couldn't agree more! The out-of-this-world scenery with gorgeous
            mountains that feel like you're in a movie, stunning beaches, and
            did someone say Fairy Pools? You'll feel like you've escaped to an
            enchanted world. This summer is the perfect time (and weather) to
            experience it! Wanna make it an awesome road trip, camp in style and
            check out the other hidden treasures of the Scottish highlands, like
            the home of Nessie? Well, you can live your best life and win a trip
            with our{' '}
            <a
              href="https://www.daymade.co.uk/prizes/details/vw-campervan-roadtrip-2022"
              target="_blank"
              rel="noreferrer"
            >
              VW Campervan
            </a>{' '}
            to make your camping getaway even more special.
          </p>

          <img
            src={`${IMAGE_DIRECTORY}/secondary4.jpg`}
            alt="A woman with her hands in the air facing the national park on the Isle of Skye"
            className="singleBlogImage"
          />

          <h2>Lake District</h2>

          <p>
            We know the Lakes have been mentioned time and time again, but hear
            us out! Cool Camping has done some digging around to find the
            loveliest non-touristy camp spots on tranquil farms so you can
            finally enjoy the majestic lakes! Even better, these campsites don't
            make you feel like you're a camper. You have your own waterfalls and
            streams and are surrounded by glorious scenery. All you decide is
            which of the relaxing walks you want to take or get your hiking
            boots on to climb Scafell Pike, the highest peak in England!
            Whatever your dream adventure is, relaxing in the lakes feels
            romantic and offers some of the best British natural beauty. If
            Beatrix Potter and many more authors were inspired by the Lakes,
            then you've gotta head on down! Who knows, maybe you'll get your
            creative on whilst you're there!
          </p>

          <img
            src={`${IMAGE_DIRECTORY}/secondary5.jpg`}
            alt="A landscape view of the mountains in the Lake District"
            className="singleBlogImage"
          />

          <p>
            We hope you've got your camping planning head on for your fun summer
            getaway. Camping has become even cooler, and reconnecting with
            nature is hot on everyone's list. It's so cool that you can win a
            free UK holiday with{' '}
            <a
              href="https://www.daymade.co.uk/prizes/details/cool-camping-300"
              target="_blank"
              rel="noreferrer"
            >
              Cool Camping
            </a>
            , so you have no excuses now. Go on, get that tent out and have the
            best summer ever! DAYMADE has tons of prizes to be won every week,
            and camping is one of the best experiences to make your day.
          </p>

          <img
            src={`${IMAGE_DIRECTORY}/secondary6.jpg`}
            alt="A group of friends camping and sat around a campfire"
            className="singleBlogImage"
          />

          <Separator style={{ margin: '1em 0 2.5em' }} />

          <p>
            Want to win your ultimate camping trip? Enter the DAYMADE weekly
            prize draw for your chance to win to make your summer unforgettable.
          </p>

          <RoundedButton
            href={getCta()?.url}
            style={{ minWidth: '180px', margin: '1em 0 1.5em' }}
          >
            Enter Now
          </RoundedButton>
        </>
      </BlogArticle>
    </MainLayout>
  );
};

export default Article;
